import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import { FormattedMessage, injectIntl } from 'react-intl'
import { isAddressComplete } from '@vtex/delivery-packages/dist/address'
import pick from 'lodash/pick'
import isEmpty from 'lodash/isEmpty'
import Input from '@vtex/styleguide/lib/Input'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import { ANONYMOUS_NAME } from '@/checkout-instore-legacy/constants/IdentificationConstants'
import SummaryView from '@/checkout-instore-legacy/components/Commons/Summary/containers/SummaryViewContainer'
import Link from '@/checkout-instore-legacy/components/Commons/Elements/Link'
import Checkbox from '@/checkout-instore-legacy/components/Commons/Elements/Checkbox'
import SectionTitle from '@/checkout-instore-legacy/components/Commons/Elements/SectionTitle'
import FooterButton from '@/checkout-instore-legacy/components/Commons/Elements/FooterButton'
import withOrderFormAndVendor from '@/checkout-instore-legacy/order/withOrderFormAndVendor'
import { getClientProfileFullName } from '@/checkout-instore-legacy/customer/utils'
import {
  getPickupOnlyLogisticsInfo,
  sanitizedNeighborhood,
} from '@/checkout-instore-legacy/shipping/utils'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import PostalCodeBar from '../PostalCodeBar'
import withOrderFormNotEmpty from '@/checkout-instore-legacy/order/withOrderFormNotEmpty'

class Address extends Component {
  constructor(props) {
    super(props)

    const { intl } = props
    const { address } = props.shipping

    this.state = {
      addressSameReceiver: true,
      isAddressCollapsed: this.shouldCollapseAddress(address),
      street: address.street || '',
      neighborhood: sanitizedNeighborhood(address.neighborhood) || '',
      city: address.city || '',
      state: address.state || '',
      number: address.number || '',
      complement: address.complement || '',
      reference: address.reference || '',
      receiverName: this.getInitialReceiverName(),
    }

    this.labels = {
      street: intl.formatMessage({
        id: 'shippingAddressStreet',
      }),
      neighborhood: intl.formatMessage({
        id: 'shippingAddressNeighborhood',
      }),
      city: intl.formatMessage({
        id: 'shippingAddressCity',
      }),
      state: intl.formatMessage({
        id: 'shippingAddressState',
      }),
      number: intl.formatMessage({
        id: 'shippingAddressNumber',
      }),
      complement: intl.formatMessage({
        id: 'shippingAddressComplement',
      }),
      reference: intl.formatMessage({
        id: 'shippingAddressReference',
      }),
      receiverName: intl.formatMessage({
        id: 'shippingAddressReceiver',
      }),
    }

    this.placeholders = {
      complement: intl.formatMessage({
        id: 'shippingAddressComplementPlaceHolder',
      }),
    }

    this.isPickupOnlyLogisticsInfo = false

    if (!isEmpty(props.shipping.logisticsInfo)) {
      const pickupOnlyLogisticsInfo = getPickupOnlyLogisticsInfo(
        props.shipping.logisticsInfo
      )
      this.isPickupOnlyLogisticsInfo =
        pickupOnlyLogisticsInfo.length === props.shipping.logisticsInfo.length
    }
  }

  /**
   * Some countries have different usages of postal codes.
   * In Mexico, for example, a single postal code can be
   * mapped to more than one neighborhood. When this happens,
   * Checkout sends the field `neighborhoods` in the following
   * format: 'NEIGHBORHOOD_1::NEIBORHOOD_2::...::NEIBORHOOD_N'.
   * The line below looks for that pattern and changes it so
   * it will be more user-friendly.
   */

  shouldCollapseAddress(address) {
    const { street, neighborhood, city, state } = address || {}
    return !!(street && neighborhood && city && state)
  }

  getInitialReceiverName() {
    const { address } = this.props.shipping
    const receiverName = (address && address.receiverName) || ''
    return receiverName === ANONYMOUS_NAME ? '' : receiverName
  }

  getReceiverName() {
    const { receiverName, addressSameReceiver } = this.state
    const { clientProfileData } = this.props.orderForm
    const fullName = getClientProfileFullName(clientProfileData)

    if (!receiverName.trim()) {
      return fullName
    }

    return addressSameReceiver ? fullName : receiverName
  }

  handleToggleAddressCollapsed = (e) => {
    e.preventDefault()
    this.setState({ isAddressCollapsed: !this.state.isAddressCollapsed })
  }

  onInputChange = (stateId, value) => {
    this.setState({ [stateId]: value })
  }

  handleSubmit = (e) => {
    e.preventDefault()

    if (
      !isAddressComplete(this.state) ||
      !this.isComplementValid(this.state.complement) ||
      !this.isReceiverValid(this.state.complement)
    ) {
      return
    }

    const {
      router,
      sendAddressOnShippingData,
      shipping: { address },
    } = this.props

    const updatedAddress = {
      ...address,
      ...pick(this.state, [
        'number',
        'complement',
        'reference',
        'street',
        'neighborhood',
        'city',
        'state',
      ]),
      receiverName: this.getReceiverName(),
    }

    sendAddressOnShippingData(updatedAddress)

    router.push('/payment')
  }

  handleSameReceiverCheck = () => {
    this.setState({ addressSameReceiver: !this.state.addressSameReceiver })
  }

  renderFooterButton = () => {
    const { isOnline, waitingForGiftCardArrival } = this.props
    const isDisabled =
      !isOnline ||
      !isAddressComplete(this.state) ||
      !this.isComplementValid(this.state.complement) ||
      !this.isReceiverValid() ||
      !!waitingForGiftCardArrival

    return (
      <FooterButton
        id="go-from-address-to-payment"
        className="absolute-l bottom-0-l right-0-l w-25-l lh-copy-l"
        disabled={isDisabled}
        type="submit"
        kind="success"
      >
        <FormattedMessage id="cartGoToPayment" />
      </FooterButton>
    )
  }

  isComplementValid = (complement) => {
    const hasNotAllowedChar = /<|>|\?|\+|"|;|%/.test(complement)

    return !hasNotAllowedChar
  }

  isReceiverValid = () => {
    const { receiverName, addressSameReceiver } = this.state

    if (!addressSameReceiver) {
      return !!receiverName.trim()
    }

    return true
  }

  renderInput = (name, autoFocus = false, type = 'text') => {
    const value = this.state[name]
    const label = this.labels[name]
    const placeholder = this.placeholders[name]
    return (
      <div className="pb4">
        <Input
          id={`address-${name.toLowerCase()}-input`}
          value={value}
          label={label}
          type={type}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onChange={(e) => this.onInputChange(name, e.target.value)}
          error={name === 'complement' && !this.isComplementValid(value)}
          errorMessage={
            (name === 'complement' &&
              !this.isComplementValid(value) &&
              this.props.intl.formatMessage({
                id: 'shippingAddressComplementNotAllowed',
              })) ||
            ''
          }
          size="large"
        />
      </div>
    )
  }

  render() {
    const {
      number,
      street,
      neighborhood,
      city,
      state,
      addressSameReceiver,
      isAddressCollapsed,
    } = this.state
    const { showAddressReference } = getInstoreConfig()
    const {
      shipping: {
        address: { postalCode },
      },
    } = this.props

    return (
      <SummaryView
        containerType="form"
        onSubmit={this.handleSubmit}
        renderFooterButton={this.renderFooterButton}
      >
        <div className="flex-auto overflow-auto momentum bg-light-silver">
          <div className="flex-none bg-base">
            <PostalCodeBar postalCode={postalCode} />
          </div>
          <div className="pt4">
            {isAddressCollapsed ? (
              <div className="f5 f5-ns black mb5 mb6-ns">
                <SectionTitle>
                  {this.isPickupOnlyLogisticsInfo ? (
                    <FormattedMessage id="shippingInvoiceAddressLabel" />
                  ) : (
                    <FormattedMessage id="shippingAddressLabel" />
                  )}
                </SectionTitle>
                <div className="ph5 ph6-l lh-copy f4-ns">{street}</div>
                <div className="ph5 ph6-l lh-copy f4-ns">
                  {neighborhood}
                  {' - '}
                  {city}
                  {' - '}
                  {state}
                  <Link
                    onClick={(e) => this.handleToggleAddressCollapsed(e)}
                    className="ml4 f4-ns f6 mr5-l"
                    intlId="shippingAddressChangeLabel"
                  />
                </div>
              </div>
            ) : (
              <div className="pt4 ph5 ph6-l">
                {this.renderInput('street', !street)}
                {this.renderInput('neighborhood')}
                {this.renderInput('city')}
                {this.renderInput('state')}
              </div>
            )}

            <div className="flex items-end pt5 ph5 ph6-l">
              <div className="flex-none w3 w4-ns mr4 mr5-ns">
                {this.renderInput(
                  'number',
                  isAddressCollapsed && !number,
                  'tel'
                )}
              </div>
              <div className="flex-auto">{this.renderInput('complement')}</div>
            </div>
            {showAddressReference && (
              <div className="pt5 ph5 ph6-l">
                {this.renderInput('reference')}
              </div>
            )}
            {!this.isPickupOnlyLogisticsInfo && (
              <div className="pt4 ph5 ph6-l">
                <Checkbox
                  id="addressSameReceiver"
                  onClick={this.handleSameReceiverCheck}
                >
                  <FormattedMessage id="shippingAddressSameReceiver" />
                </Checkbox>
              </div>
            )}
            {!addressSameReceiver && (
              <div id="addressReceiverNameContainer" className="pt4 ph5 ph6-l">
                {this.renderInput('receiverName')}
              </div>
            )}
          </div>
        </div>
      </SummaryView>
    )
  }
}

Address.propTypes = {
  sendAddressOnShippingData: PropTypes.func,
  shipping: PropTypes.object,
  router: PropTypes.object,
  orderForm: PropTypes.object,
  intl: intlShape.isRequired,
  waitingForGiftCardArrival: PropTypes.bool,
  isOnline: PropTypes.bool,
}

Address.defaultProps = {
  isOnline: true,
}

export default withRouter(
  injectIntl(withOrderFormAndVendor(withOrderFormNotEmpty(Address)))
)
